import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchErrorPatternById,
  updateErrorPattern,
  fetchErrorPatternEnums,
} from '../api/features/errorPattern/errorPatternApi';
import { fetchClients as fetchClientsApi } from '../api/features/clientData/clientApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: SAP_COLORS.sapWhite,
    '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
    '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
    '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue },
  },
  '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
  '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7 },
};

const disabledInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: SAP_COLORS.sapGray1,
    '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
  },
  '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
  '& .MuiInputBase-input': { color: SAP_COLORS.sapGray6 },
};

export default function ErrorPatternEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { clientCode } = useClientCode();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pattern, setPattern] = useState<any>(null);
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [errorTypeOptions, setErrorTypeOptions] = useState<any[]>([]);

  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedErrorType, setSelectedErrorType] = useState<any>(null);
  const [patternText, setPatternText] = useState('');
  const [errorCode, setErrorCode] = useState('');

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [clients, enums] = await Promise.all([
          fetchClientsApi({ clientCode: 'admin', firstResult: -1, maxResult: 0 }),
          fetchErrorPatternEnums({ clientCode }),
        ]);
        setClientOptions((clients || []).map((c: any) => ({ label: c.name, value: c.id, code: c.code })));
        if (enums && Array.isArray(enums)) {
          const opts = enums
            .map((arr: any) => (Array.isArray(arr) ? arr : []))
            .flat()
            .map((t: string) => ({ label: t, value: t }));
          setErrorTypeOptions(opts);
        } else if (enums && typeof enums === 'object') {
          setErrorTypeOptions(Object.keys(enums).map((k) => ({ label: k, value: k })));
        }
      } catch (err) {
        logger.error('Failed to load options:', err);
      }
    };
    loadOptions();
  }, [clientCode]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await fetchErrorPatternById({ clientCode, patternId: Number(id) });
        setPattern(data);
        const clientId = (data as any).clientId;
        setSelectedClient({ label: (data as any).clientName || 'Unknown', value: clientId });
        const isTech = (data as any).isTechnicalError;
        const errorTypeVal =
          isTech === true || isTech === 'true'
            ? 'TECHNICAL_ERROR'
            : isTech === false || isTech === 'false'
              ? 'BUSINESS_ERROR'
              : (data as any).errorType ?? (data as any).isTechnicalError ?? '';
        setSelectedErrorType(
          errorTypeOptions.find((o: any) => o.value === errorTypeVal) || { label: errorTypeVal || 'Default', value: errorTypeVal || '' }
        );
        setPatternText((data as any).pattern || '');
        setErrorCode((data as any).errorCode || '');
      } catch (err) {
        logger.error('Failed to fetch error pattern:', err);
        toast.error('Failed to load error pattern');
        navigate('/error-pattern');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, clientCode]);

  useEffect(() => {
    if (!pattern || clientOptions.length === 0) return;
    const clientId = (pattern as any).clientId;
    const match = clientOptions.find((c: any) => c.value === clientId);
    if (match) setSelectedClient(match);
  }, [pattern, clientOptions]);

  const handleBack = () => navigate('/error-pattern');

  const handleSave = async () => {
    if (!id || !selectedClient?.value) {
      toast.error('Client is required');
      return;
    }
    if (!selectedErrorType?.value) {
      toast.error('Error type is required');
      return;
    }
    if (!patternText.trim()) {
      toast.error('Pattern is required');
      return;
    }
    if (!errorCode.trim()) {
      toast.error('Error code is required');
      return;
    }
    setSaving(true);
    try {
      const p = pattern as any;
      const now = Date.now();
      const payload = {
        id: Number(id),
        clientId: Number(selectedClient.value),
        errorCode: errorCode.trim(),
        pattern: patternText.trim(),
        isTechnicalError: selectedErrorType.value,
        isRetryRequired: p?.isRetryRequired ?? null,
        createdBy: p?.createdBy ?? 0,
        creationDate: p?.creationDate ?? now,
        lastUpdateDate: now,
        lastUpdatedBy: p?.lastUpdatedBy ?? p?.createdBy ?? 0,
      };
      await updateErrorPattern({ clientCode, payload });
      toast.success('Error pattern updated successfully');
      // Stay on edit page - refetch to show updated data
      const updated = await fetchErrorPatternById({ clientCode, patternId: Number(id) });
      const u = updated as any;
      setPattern(updated);
      setPatternText(u?.pattern ?? patternText);
      setErrorCode(u?.errorCode ?? errorCode);
      const errType = u?.isTechnicalError;
      const errVal = errType === true || errType === 'true' ? 'TECHNICAL_ERROR' : errType === false || errType === 'false' ? 'BUSINESS_ERROR' : errType ?? '';
      setSelectedErrorType(
        errorTypeOptions.find((o: any) => o.value === errVal) || { label: errVal || 'Default', value: errVal || '' }
      );
      const cId = u?.clientId;
      const cMatch = clientOptions.find((c: any) => c.value === cId);
      if (cMatch) setSelectedClient(cMatch);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update error pattern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={48} />
      </Box>
    );
  }

  if (!pattern) return null;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: SAP_COLORS.sapGray1,
        minHeight: '100vh',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: SAP_SPACING.lg }}>
        <IconButton onClick={handleBack} aria-label="Back" sx={{ color: SAP_COLORS.sapGray7, '&:hover': { bgcolor: SAP_COLORS.sapGray2 } }}>
          <ArrowBackIcon />
        </IconButton>
        <ErrorOutlineIcon sx={{ color: SAP_COLORS.sapBlue, fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: SAP_TYPOGRAPHY.fontWeight.bold, color: SAP_COLORS.sapGray7 }}>
          Edit Error Pattern
        </Typography>
      </Box>

      <Card
        sx={{
          borderRadius: '8px',
          border: `1px solid ${SAP_COLORS.sapGray2}`,
          boxShadow: SAP_SHADOWS.card,
          width: '100%',
          maxWidth: 720,
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
              <TextField
                label="Client"
                value={selectedClient?.label ?? ''}
                size="small"
                fullWidth
                disabled
                sx={disabledInputSx}
              />
            </Box>
            <Box>
              <Autocomplete
                options={errorTypeOptions}
                value={selectedErrorType}
                onChange={(_, v) => setSelectedErrorType(v)}
                getOptionLabel={(o) => o?.label ?? ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Error Type"
                    size="small"
                    required
                    fullWidth
                    placeholder="e.g. TECHNICAL_ERROR, BUSINESS_ERROR"
                    sx={inputSx}
                  />
                )}
                disabled={saving}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
              />
            </Box>
            <Box>
              <TextField
                label="Error Code"
                value={errorCode}
                onChange={(e) => setErrorCode(e.target.value)}
                size="small"
                required
                fullWidth
                disabled={saving}
                sx={inputSx}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
              <TextField
                label="Pattern"
                value={patternText}
                onChange={(e) => setPatternText(e.target.value)}
                size="small"
                required
                fullWidth
                multiline
                minRows={2}
                disabled={saving}
                sx={inputSx}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mt: 3,
              pt: 2,
              borderTop: `1px solid ${SAP_COLORS.sapGray3}`,
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={handleBack} disabled={saving} sx={{ color: SAP_COLORS.sapGray6, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !patternText.trim() || !errorCode.trim() || !selectedErrorType?.value || !selectedClient?.value}
              sx={{
                backgroundColor: SAP_COLORS.sapBlue,
                '&:hover': { backgroundColor: SAP_COLORS.sapDarkBlue },
                textTransform: 'none',
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
